/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { frame } from '../flight/state.ts'
import { DESKS, DESK_TOP_Y } from '../world.ts'
import { SPILL_SEGMENTS } from './tubesData.ts'
import { SURFACE1, SURFACE2, CYAN_WASH } from '../palette.ts'

function washAt(x: number, z: number): number {
  let w = 0
  for (const seg of SPILL_SEGMENTS) {
    const ax = seg.a.x, az = seg.a.z, bx = seg.b.x, bz = seg.b.z
    const t = Math.max(0, Math.min(1,
      ((x - ax) * (bx - ax) + (z - az) * (bz - az)) /
        ((bx - ax) ** 2 + (bz - az) ** 2 + 1e-6)))
    const px = ax + (bx - ax) * t
    const pz = az + (bz - az) * t
    const d2 = (x - px) ** 2 + (z - pz) ** 2 + (seg.a.y - 0.9) ** 2 * 0.5
    w += Math.exp(-d2 * 0.12)
  }
  return Math.min(w, 1)
}

// FOUNDATION §6.2 — one instanced mesh per prop type; a desk is a box and four legs.
// Instancing is done with explicit per-instance attributes (aOffset/aYaw/aBirth) rather
// than InstancedMesh.instanceMatrix — see ISSUES.md #8 for why. Per-instance aBirth +
// the uReveal track grows the world outward from the hero desk.

const VERT = /* glsl */ `
attribute vec3 aOffset;
attribute float aYaw;
attribute float aBirth;
attribute float aWash;
uniform float uReveal, uFog, uChroma;
uniform vec3 uBg, uColor, uWashCol;
varying vec3 vCol;
void main() {
  float s = smoothstep(aBirth, aBirth + 0.03, uReveal);
  float c = cos(aYaw), sn = sin(aYaw);
  mat3 R = mat3(c, 0.0, -sn, 0.0, 1.0, 0.0, sn, 0.0, c);
  vec3 wp = R * (position * s) + aOffset;
  vec4 mv = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = R * normal;
  float l = 0.74 + 0.26 * max(dot(n, normalize(vec3(0.35, 1.0, 0.5))), 0.0);
  // §4.2 mechanism 1: white surfaces near a light run pick up --cyan-wash —
  // without this, tubes are painted plastic; with it, they emit
  vec3 col = mix(uColor, uWashCol, aWash * uChroma * 0.85) * l;
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(lum), clamp(fo * 1.7, 0.0, 1.0));
  col = mix(col, uBg, fo);
  vCol = col;
}
`
const FRAG = /* glsl */ `
varying vec3 vCol;
void main() { gl_FragColor = vec4(vCol, 1.0); }
`

function deskGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = []
  const top = new THREE.BoxGeometry(1.4, 0.05, 0.7)
  top.translate(0, DESK_TOP_Y - 0.025, 0)
  parts.push(top)
  for (const sx of [-1, 1])
    for (const sz of [-1, 1]) {
      const leg = new THREE.BoxGeometry(0.05, 0.72, 0.05)
      leg.translate(sx * 0.64, 0.36, sz * 0.3)
      parts.push(leg)
    }
  return mergeGeometries(parts)!
}

function monitorGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = []
  const screen = new THREE.BoxGeometry(0.6, 0.36, 0.035)
  screen.translate(0, DESK_TOP_Y + 0.33, 0)
  parts.push(screen)
  const stand = new THREE.BoxGeometry(0.06, 0.15, 0.06)
  stand.translate(0, DESK_TOP_Y + 0.075, 0)
  parts.push(stand)
  const foot = new THREE.BoxGeometry(0.22, 0.02, 0.16)
  foot.translate(0, DESK_TOP_Y + 0.01, 0)
  parts.push(foot)
  return mergeGeometries(parts)!
}

function chairGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = []
  const seat = new THREE.BoxGeometry(0.5, 0.06, 0.48)
  seat.translate(0, 0.45, 0)
  parts.push(seat)
  const back = new THREE.BoxGeometry(0.48, 0.5, 0.06)
  back.translate(0, 0.73, 0.24)
  parts.push(back)
  const post = new THREE.CylinderGeometry(0.03, 0.03, 0.42, 6)
  post.translate(0, 0.21, 0)
  parts.push(post)
  const base = new THREE.CylinderGeometry(0.24, 0.26, 0.03, 10)
  base.translate(0, 0.02, 0)
  parts.push(base)
  return mergeGeometries(parts)!
}

interface Placed {
  x: number
  z: number
  yaw: number
  birth: number
}

function instancedFrom(
  base: THREE.BufferGeometry,
  placed: Placed[],
  color: THREE.Color,
): { geometry: THREE.InstancedBufferGeometry; material: THREE.ShaderMaterial } {
  const N = placed.length
  const geometry = new THREE.InstancedBufferGeometry()
  geometry.index = base.index
  geometry.setAttribute('position', base.attributes.position)
  geometry.setAttribute('normal', base.attributes.normal)
  const offsets = new Float32Array(N * 3)
  const yaws = new Float32Array(N)
  const births = new Float32Array(N)
  const washes = new Float32Array(N)
  placed.forEach((d, i) => {
    offsets[i * 3] = d.x
    offsets[i * 3 + 1] = 0
    offsets[i * 3 + 2] = d.z
    yaws[i] = d.yaw
    births[i] = d.birth
    washes[i] = washAt(d.x, d.z)
  })
  geometry.setAttribute('aOffset', new THREE.InstancedBufferAttribute(offsets, 3))
  geometry.setAttribute('aYaw', new THREE.InstancedBufferAttribute(yaws, 1))
  geometry.setAttribute('aBirth', new THREE.InstancedBufferAttribute(births, 1))
  geometry.setAttribute('aWash', new THREE.InstancedBufferAttribute(washes, 1))
  geometry.instanceCount = N
  const material = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uReveal: { value: 0 },
      uFog: { value: 0.016 },
      uChroma: { value: 0 },
      uBg: { value: new THREE.Color() },
      uColor: { value: color.clone() },
      uWashCol: { value: CYAN_WASH.clone() },
    },
  })
  return { geometry, material }
}

export function Props() {
  const items = useMemo(() => {
    // the hero desk (index 0) is the cutout's own furniture — no box props there
    const FIELD = DESKS.slice(1)
    const deskPlaced: Placed[] = FIELD.map((d) => ({ x: d.x, z: d.z, yaw: d.yaw, birth: d.birth }))
    const chairPlaced: Placed[] = FIELD.map((d) => ({
      x: d.x - Math.sin(d.yaw) * 0.75,
      z: d.z - Math.cos(d.yaw) * 0.75,
      yaw: d.yaw,
      birth: d.birth,
    }) as Placed)
    return [
      instancedFrom(deskGeometry(), deskPlaced, SURFACE1),
      instancedFrom(monitorGeometry(), deskPlaced, SURFACE1),
      instancedFrom(chairGeometry(), chairPlaced, SURFACE2),
    ]
  }, [])

  useFrame(() => {
    const b = frame.beats
    for (const { material } of items) {
      material.uniforms.uReveal.value = b.reveal
      material.uniforms.uFog.value = b.fog
      material.uniforms.uChroma.value = b.chroma
      ;(material.uniforms.uBg.value as THREE.Color).copy(frame.bg)
    }
  })

  return (
    <>
      {items.map((it, i) => (
        <mesh key={i} geometry={it.geometry} material={it.material} frustumCulled={false} />
      ))}
    </>
  )
}
